/**
 * Nutri Tailor Select Extension for Voiceflow
 * 
 * A searchable dropdown extension for capturing country/state/region selections
 * in the Voiceflow web chat widget.
 * 
 * Usage:
 * 1. Add this script to your Shopify theme BEFORE the Voiceflow widget script
 * 2. Register in the widget: extensions: [NutriSelectExtension]
 * 3. Trigger with Custom Action step named "ext_select"
 * 
 * Created: 29 January 2026
 */

const NutriSelectExtension = {
  name: 'NutriSelect',
  type: 'response',
  
  // Match when Voiceflow sends a trace with type 'ext_select'
  match: ({ trace }) => 
    trace.type === 'ext_select' || trace.payload?.name === 'ext_select',
  
  render: ({ trace, element }) => {
    // Parse payload from Voiceflow Custom Action
    const payload = typeof trace.payload === 'string' 
      ? JSON.parse(trace.payload) 
      : trace.payload;
    
    // Extract configuration
    const options = payload.options || [];
    const placeholder = payload.placeholder || 'Search...';
    const title = payload.title || 'Select an option';
    const showFlags = payload.showFlags !== false; // Default true for countries
    const maxHeight = payload.maxHeight || '200px';
    
    // Disable the main chat input while selector is active
    disableChatInput(true);
    
    // Create container
    const container = document.createElement('div');
    container.className = 'nutri-select-wrapper';
    
    container.innerHTML = `
      <style>
        .nutri-select-wrapper {
          font-family: 'General Sans', -apple-system, BlinkMacSystemFont, sans-serif;
          width: 100%;
          margin: 8px 0;
        }
        
        .nutri-select-title {
          font-size: 14px;
          font-weight: 500;
          color: #34322a;
          margin-bottom: 8px;
        }
        
        .nutri-select-container {
          background: #fcf8f0;
          border: 1px solid #99958a;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .nutri-select-search {
          width: 100%;
          padding: 12px 12px 12px 36px;
          border: none;
          border-bottom: 1px solid #f2eee1;
          background: #ffffff;
          font-size: 14px;
          color: #34322a;
          outline: none;
          box-sizing: border-box;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2399958a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'%3E%3C/circle%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'%3E%3C/line%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: 12px center;
        }
        
        .nutri-select-search::placeholder {
          color: #99958a;
        }
        
        .nutri-select-search:focus {
          background-color: #ffffff;
        }
        
        .nutri-select-list {
          max-height: ${maxHeight};
          overflow-y: auto;
          background: #fcf8f0;
        }
        
        .nutri-select-item {
          padding: 10px 12px;
          cursor: pointer;
          font-size: 14px;
          color: #34322a;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background-color 0.15s ease;
        }
        
        .nutri-select-item:hover {
          background: #f2eee1;
        }
        
        .nutri-select-item:active {
          background: #e8e4d7;
        }
        
        .nutri-select-item-flag {
          font-size: 16px;
          width: 20px;
          text-align: center;
        }
        
        .nutri-select-item-label {
          flex: 1;
        }
        
        .nutri-select-empty {
          padding: 16px 12px;
          text-align: center;
          color: #99958a;
          font-size: 14px;
        }
        
        .nutri-select-list::-webkit-scrollbar {
          width: 6px;
        }
        
        .nutri-select-list::-webkit-scrollbar-track {
          background: #f2eee1;
        }
        
        .nutri-select-list::-webkit-scrollbar-thumb {
          background: #99958a;
          border-radius: 3px;
        }
        
        .nutri-select-list::-webkit-scrollbar-thumb:hover {
          background: #5f5b51;
        }
      </style>
      
      <div class="nutri-select-title">${escapeHtml(title)}</div>
      <div class="nutri-select-container">
        <input 
          type="text" 
          class="nutri-select-search" 
          placeholder="${escapeHtml(placeholder)}"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
        >
        <div class="nutri-select-list"></div>
      </div>
    `;
    
    const searchInput = container.querySelector('.nutri-select-search');
    const list = container.querySelector('.nutri-select-list');
    
    // Country code to flag emoji mapping
    function getFlag(countryCode) {
      if (!countryCode || countryCode.length !== 2) return '';
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    }
    
    // Escape HTML to prevent XSS
    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
    
    // Render filtered options
    function renderOptions(filter = '') {
      const searchTerm = filter.toLowerCase().trim();
      
      const filtered = searchTerm 
        ? options.filter(opt => {
            const label = (opt.label || opt.name || '').toLowerCase();
            const value = (opt.value || opt.code || '').toLowerCase();
            return label.includes(searchTerm) || value.includes(searchTerm);
          })
        : options;
      
      if (filtered.length === 0) {
        list.innerHTML = `<div class="nutri-select-empty">No results found</div>`;
        return;
      }
      
      list.innerHTML = filtered.map(opt => {
        const value = opt.value || opt.code || opt.country_code || '';
        const label = opt.label || opt.name || opt.country_name || '';
        const flag = showFlags ? getFlag(value) : '';
        
        return `
          <div class="nutri-select-item" data-value="${escapeHtml(value)}" data-label="${escapeHtml(label)}">
            ${flag ? `<span class="nutri-select-item-flag">${flag}</span>` : ''}
            <span class="nutri-select-item-label">${escapeHtml(label)}</span>
          </div>
        `;
      }).join('');
    }
    
    // Initial render
    renderOptions();
    
    // Filter as user types
    searchInput.addEventListener('input', (e) => {
      renderOptions(e.target.value);
    });
    
    // Handle keyboard navigation
    let selectedIndex = -1;
    
    searchInput.addEventListener('keydown', (e) => {
      const items = list.querySelectorAll('.nutri-select-item');
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
        updateSelection(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIndex = Math.max(selectedIndex - 1, 0);
        updateSelection(items);
      } else if (e.key === 'Enter' && selectedIndex >= 0) {
        e.preventDefault();
        const item = items[selectedIndex];
        if (item) {
          selectItem(item.dataset.value, item.dataset.label);
        }
      }
    });
    
    function updateSelection(items) {
      items.forEach((item, i) => {
        if (i === selectedIndex) {
          item.style.background = '#f2eee1';
          item.scrollIntoView({ block: 'nearest' });
        } else {
          item.style.background = '';
        }
      });
    }
    
    // Handle click selection
    list.addEventListener('click', (e) => {
      const item = e.target.closest('.nutri-select-item');
      if (item) {
        selectItem(item.dataset.value, item.dataset.label);
      }
    });
    
    // Select and submit
    function selectItem(value, label) {
      // Re-enable chat input
      disableChatInput(false);
      
      // Hide the selector
      container.style.display = 'none';
      
      // Send selection back to Voiceflow
      window.voiceflow.chat.interact({
        type: 'complete',
        payload: { 
          value: value, 
          label: label 
        }
      });
    }
    
    // Disable/enable the main chat input
    function disableChatInput(disabled) {
      try {
        const chatDiv = document.getElementById('voiceflow-chat');
        if (chatDiv && chatDiv.shadowRoot) {
          const textarea = chatDiv.shadowRoot.querySelector('textarea');
          if (textarea) {
            textarea.disabled = disabled;
            textarea.placeholder = disabled ? 'Please make a selection above...' : 'Type a message...';
            textarea.style.opacity = disabled ? '0.5' : '1';
          }
        }
      } catch (err) {
        console.warn('[NutriSelect] Could not toggle chat input:', err);
      }
    }
    
    // Append to chat
    element.appendChild(container);
    
    // Focus search input after a brief delay (allows DOM to settle)
    setTimeout(() => {
      searchInput.focus();
    }, 100);
    
    // Cleanup function (called when element is removed)
    return () => {
      disableChatInput(false);
    };
  }
};

// Make available globally for registration
if (typeof window !== 'undefined') {
  window.NutriSelectExtension = NutriSelectExtension;
}